import { SelectionMode } from '@uifabric/utilities'
import { ColumnRenderer, EntityList } from 'components/EntityList'
import { FieldProps } from 'formik'
import { Stack } from 'forms/FormStack'
import { Box } from 'grid'
import { useCurrentProving } from 'hooks/useCurrentProving'
import useSelection from 'hooks/useSelection'
import {
  Resource,
  ResourceBookingAddInput,
  ResourceRequirementBatchInput,
  ResourceRequirementStatus,
} from 'model'
import {
  Checkbox,
  ConstrainMode,
  Label,
  PrimaryButton,
} from 'office-ui-fabric-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
import {
  useAddResourceBookingMutation,
  useUpdateResourceRequirementMutation,
} from 'views/ProvingDetailsView/hooks'

export const ResourceSearchFreeResourceList: React.FC<{
  fieldProps: FieldProps<any>
  resources: any[]
  initialValues: any
}> = ({ fieldProps, resources, initialValues }) => {
  const { t } = useTranslation()
  const { form, field } = fieldProps
  const [addResourceBooking] = useAddResourceBookingMutation()
  const [updateResourceRequirement] = useUpdateResourceRequirementMutation()
  const [proving, refetch] = useCurrentProving()
  const [selection] = useSelection<any>({
    onSelectionChanged(selectedItems) {
      form.setFieldValue(field.name, selectedItems)
    },
    getKey: (item) => item._id,
  })
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const [checkboxState, setCheckboxState] = useState(true)
  const [bookingState, setBookingState] = useState(false)
  const renderResourceCategory = React.useMemo<ColumnRenderer>(
    () => (item, i, col) => t(`field.category.${item}`),
    [t]
  )
  const renderResourceType = React.useMemo<ColumnRenderer>(
    () => (item, i, col) => t(`field.type.${item}`),
    [t]
  )

  const columns = [
    fieldColumn('type', {
      onRender: (item: any, i, col) => {
        return renderResourceType(item.type)
      },
      getGroupValue: (item: any) => t(`field.type.${item.type}`),
      isGrouped: true,
      isSorted: true,

      ungroupable: true,
    }),
    fieldColumn('category', {
      onRender: (item: any, i, col) => {
        return renderResourceCategory(item.category)
      },
      ungroupable: true,
    }),
    fieldColumn('title', { ungroupable: true }),
    fieldColumn('parent.title', {
      onRender: (item: any, i, col) => {
        const value = item.parent ? item.parent!.title : ''
        return (
          <Box key={i} style={{ whiteSpace: 'pre-line' }}>
            {value}
          </Box>
        )
      },
      minWidth: 100,
      ungroupable: true,
    }),
  ]

  const handleSubmit = async () => {
    const itemsToAdd: ResourceBookingAddInput[] =
      form.values.freeResources !== undefined
        ? form.values.freeResources.map(
            (res: Resource) =>
              ({
                resource: res._id,
                dateFrom:
                  form.values.dateFrom === undefined
                    ? proving.dateFrom
                    : form.values.dateFrom,
                dateTo:
                  form.values.dateTo === undefined
                    ? proving.dateTo
                    : form.values.dateTo,
              } as ResourceBookingAddInput)
          )
        : ([] as ResourceBookingAddInput[])

    // const item = {}
    const { errors } = await addResourceBooking({
      variables: {
        provingId: proving._id,
        input: itemsToAdd,
      },
    })
    if (errors) {
      throw errors
    }

    const batch: ResourceRequirementBatchInput[] = []
    batch.push({
      _id: initialValues._id,
      update: {
        status:
          checkboxState === true
            ? ResourceRequirementStatus.Finished
            : ResourceRequirementStatus.Open,
      },
    })
    await updateResourceRequirement({
      variables: {
        provingId: proving._id,
        batch,
      },
    })
    refetch()
  }

  return (
    <>
      <EntityList
        items={resources}
        selection={selection}
        columns={columns}
        alternate={true}
        stickyHeader={true}
        selectionMode={SelectionMode.multiple}
        constrainMode={ConstrainMode.horizontalConstrained}
      />
      <Stack
        horizontal={true}
        horizontalAlign="end"
        styles={{ root: { paddingTop: 10 } }}
      >
        <Stack tokens={{ childrenGap: 5 }}>
          <Label>{`Anzahl ausgewählt: ${selection.count}`}</Label>
          <Checkbox
            label={t('field.setBookingToFinished.label')}
            defaultChecked={true}
            onChange={() => {
              setCheckboxState(!checkboxState)
            }}
          />
          <PrimaryButton
            disabled={selection.count === 0}
            onClick={() => {
              handleSubmit()
              setBookingState(true)
            }}
          >
            {t('field.bookSelectedResources.label')}
          </PrimaryButton>
          {bookingState === true && (
            <div>Ausgewählte Ressourcen wurden gebucht</div>
          )}
        </Stack>
      </Stack>
    </>
  )
}
